import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import CustomSelect from "@/shared/components/ui/Select/CustomSelect";
import StatusBorder from "@/shared/components/ui/Status/StatusBorder";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import LinkedinButton from "@/shared/components/ui/Button/LinkedinButton";
import { getCleanName, getCleanPlainText } from "@/shared/utils/accountConfig";
import {
  getCampaignInsightTheme,
  getTextColorBasedOnValue,
  getVariant,
} from "@/shared/utils/helperFunctions";
import { Dot, Info, RefreshCcw } from "lucide-react";
import React, { useCallback, useEffect, useMemo } from "react";
import { useUpdateRelationshipType } from "../../hooks/useAccountRelationship";
import {
  RELATIONSHIP_STATUS_OPTIONS,
  toRelationshipSelectedKeys,
} from "../../utils/relationshipMappingConfig";
import { Progress } from "@heroui/react";

const ENGAGEMENT_SCORE_TOOLTIP =
  "Calculated based on recent activity, responses, campaign interactions, and engagement signals";

const MappingLeftSideHeader = ({
  relationshipMappingDetail,
  isRefreshing = false,
  onRefresh,
  showRefreshButton = true,
}) => {
  const relationshipMappingId =
    relationshipMappingDetail?.relationship_mapping_id;
  const currentRelationshipStatus =
    relationshipMappingDetail?.user_relationship_status ?? null;

  const { mutate: updateRelationshipType, isPending: isUpdatingRelationship } =
    useUpdateRelationshipType();

  const selectedKeys = useMemo(
    () => toRelationshipSelectedKeys(currentRelationshipStatus),
    [currentRelationshipStatus],
  );

  const handleRelationshipChange = useCallback(
    (keys) => {
      if (isUpdatingRelationship || !relationshipMappingId) return;

      const nextValue =
        keys === "all" || !keys?.size ? null : Array.from(keys)[0];

      if (!nextValue || nextValue === currentRelationshipStatus) return;

      updateRelationshipType({
        relationshipMappingId,
        relationshipType: nextValue,
      });
    },
    [
      currentRelationshipStatus,
      isUpdatingRelationship,
      relationshipMappingId,
      updateRelationshipType,
    ],
  );

  const subtitle = useMemo(
    () =>
      getCleanPlainText(
        [
          relationshipMappingDetail?.designation,
          getCleanName(relationshipMappingDetail?.company_name),
          getCleanName(relationshipMappingDetail?.location),
        ]
          .filter(Boolean)
          .join(" • "),
      ),
    [relationshipMappingDetail],
  );

  const engagementScore = relationshipMappingDetail?.engagement_score ?? "N/A";
  const engagementTheme = getCampaignInsightTheme(
    getTextColorBasedOnValue(engagementScore),
  );

  useEffect(() => {
    if (!relationshipMappingId) return;

    console.debug("[RelationshipMapping] engagement score rendered", {
      relationshipMappingId,
      engagementScore: relationshipMappingDetail?.engagement_score,
      scoreType: typeof relationshipMappingDetail?.engagement_score,
    });
  }, [
    engagementScore,
    relationshipMappingDetail?.engagement_score,
    relationshipMappingId,
  ]);

  return (
    <div className="flex justify-between gap-4 w-full border-b border-gray-200 dark:border-gray-800 py-4">
      <div className="flex items-center gap-3.5 min-w-0">
        <StatusBorder
          variant="success"
          number={relationshipMappingDetail?.engagement_score}
        >
          <CommonAvatar
            name={relationshipMappingDetail?.contact_name}
            email={relationshipMappingDetail?.designation}
            avatarUrl=""
            size="md"
          />
        </StatusBorder>

        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2.5">
            <div className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">
              {relationshipMappingDetail?.contact_name}
            </div>
            {relationshipMappingDetail?.role_authority ? (
              <StatusChip
                label={relationshipMappingDetail.role_authority}
                withoutBg={true}
                variant={getVariant(
                  relationshipMappingDetail?.engagement_score ?? 0,
                )}
                className="text-[9px] whitespace-nowrap w-fit shrink-0"
              />
            ) : null}
            {relationshipMappingDetail?.title_verified === false ? (
              <StatusChip
                label="Unverified"
                withoutBg={true}
                variant="warning"
                tooltipContent="This title could not be confirmed on the web and may be outdated."
                className="text-[9px] whitespace-nowrap w-fit shrink-0"
              />
            ) : null}
            {relationshipMappingDetail?.linkedin_url ? (
              <LinkedinButton
                linkedinUrl={relationshipMappingDetail.linkedin_url}
              />
            ) : null}
          </div>
          {subtitle ? (
            <div className="text-gray-500 dark:text-gray-400 font-normal font-inter text-xs truncate">
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-6 w-fit shrink-0 overflow-visible">
        {showRefreshButton && (
          <button
            type="button"
            title="Refresh contact details"
            aria-label="Refresh contact details"
            onClick={onRefresh}
            disabled={!relationshipMappingId || isRefreshing}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#D5D7DA] dark:border-gray-700 bg-white dark:bg-gray-800 text-secondary dark:text-secondary-light shadow-sm shadow-gray-200 dark:shadow-none transition hover:bg-gray-50 dark:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCcw
              size={17}
              className={isRefreshing ? "animate-spin" : ""}
            />
          </button>
        )}

        <div className="flex items-center gap-2 overflow-visible relative z-20">
          <h5 className="text-[10px] font-normal text-gray-500 dark:text-gray-400 whitespace-nowrap">
            Relationship
          </h5>
          <div className="w-[80px] shrink-0">
            <CustomSelect
              options={RELATIONSHIP_STATUS_OPTIONS}
              selectedKeys={selectedKeys}
              onSelectionChange={handleRelationshipChange}
              placeholder="Select"
              size="sm"
              width="w-full"
              popoverMatchTriggerWidth={false}
              disabled={!relationshipMappingId || isUpdatingRelationship}
              showDescription={false}
              triggerClassName="border-gray-200 dark:border-gray-700 rounded-lg !px-3 !py-1.5 min-h-0"
              valueClassName="!text-[10px] font-medium text-gray-800 dark:text-gray-100 truncate"
              selectItemClassName="!text-[10px] whitespace-nowrap"
              popoverContentClassName="!py-1"
            />
          </div>
        </div>

        <div className="flex items-center">
          <div className="flex items-center gap-0.5">
            <h5 className="text-xs font-normal text-gray-500 dark:text-gray-400 whitespace-nowrap">
              Engagement Score
            </h5>
            <CustomCommonTooltip content={ENGAGEMENT_SCORE_TOOLTIP}>
              <Info
                className="text-gray-500 dark:text-gray-400 w-3.5 h-3.5 cursor-pointer"
                strokeWidth={1.5}
              />
            </CustomCommonTooltip>
          </div>
          <Dot className="text-gray-400 dark:text-gray-500 w-3.5 h-3.5" />
          <div className="flex flex-col gap-0.5">
            <span
              className={`text-xl font-inter font-semibold ${engagementTheme.textClass}`}
            >
              {engagementScore}
            </span>
            <Progress
              value={typeof engagementScore === "number" ? engagementScore : 0}
              color={engagementTheme.progressColor}
              classNames={{
                base: "w-full h-[3px] rounded",
                indicator: engagementTheme.progressColor,
                track: "bg-gray-200 dark:bg-gray-700",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MappingLeftSideHeader;
